import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Radar, RefreshCw, ShieldCheck, Clock, AlertTriangle, TrendingUp, Lightbulb,
  ArrowRight, CalendarClock, Ban, CheckCircle2, Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PriorityBadge, Card, CardHeader, EmptyState } from "@/components/common";
import { relativeTime } from "@/lib/format";

const PRED_STYLE = {
  approve: "text-st-success border-st-success bg-st-success/10",
  revise: "text-st-warning border-st-warning bg-st-warning/10",
  reject: "text-st-critical border-st-critical bg-st-critical/10",
  review: "text-st-info border-st-info bg-st-info/10",
};

export default function ExecutiveBrief() {
  const { companyParam, companyId } = useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [period, setPeriod] = useState("weekly");
  const [busy, setBusy] = useState(false);

  const { data: summaries = [], isLoading } = useQuery({
    queryKey: ["summaries", period],
    queryFn: async () => (await api.get("/intelligence/summaries", { params: { period } })).data,
  });
  const brief = summaries[0];

  const regenerate = async () => {
    setBusy(true);
    try {
      await api.post("/intelligence/executive-summary/generate", null, { params: { period } });
      await qc.invalidateQueries({ queryKey: ["summaries"] });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="brief-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.12em] uppercase text-ink-muted">
            <Radar className="w-3.5 h-3.5 text-brand" /> Executive Brief
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink mt-1">Your situation, consolidated.</h1>
          <p className="text-sm text-ink-muted mt-1">One organized read-out so you act on what matters — not a wall of notifications.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-border">
            {["daily", "weekly"].map((p) => (
              <button key={p} onClick={() => setPeriod(p)} data-testid={`brief-period-${p}`}
                className={`px-3 py-2 text-xs font-semibold uppercase tracking-wider ${period === p ? "bg-ink text-white" : "text-ink-muted hover:bg-surface-muted"}`}>{p}</button>
            ))}
          </div>
          <button onClick={regenerate} disabled={busy} data-testid="regenerate-brief-btn"
            className="flex items-center gap-2 border border-border px-3 py-2 text-sm hover:bg-surface-muted disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        </div>
      </div>

      {isLoading ? <div className="text-sm text-ink-muted">Loading brief…</div> : !brief ? (
        <EmptyState icon={Radar} title="No brief yet" hint="Generate your first executive brief." />
      ) : (
        <>
          {/* Headline */}
          <div className="bg-ink text-white p-5 mb-4">
            <div className="text-[11px] uppercase tracking-[0.16em] text-white/50 mb-1 flex items-center gap-2">
              {period} brief · generated {relativeTime(brief.generated_at)}
              {brief.llm && <span className="text-[10px] font-semibold tracking-wider border border-white/30 text-white/80 px-1.5 py-0.5">Claude Opus 4.8</span>}
            </div>
            <div className="font-heading text-2xl font-bold tracking-tight">{brief.headline}</div>
            <div className="flex flex-wrap gap-2 mt-4">
              <Chip label="Decisions" value={brief.metrics.decisions_needed} />
              <Chip label="Deadline Risk" value={brief.metrics.deadline_risk} warn={brief.metrics.deadline_risk > 0} />
              <Chip label="Blocked / Stale" value={brief.metrics.blocked} warn={brief.metrics.blocked > 0} />
              <Chip label="Delivered" value={brief.metrics.delivered_this_period} />
              <Chip label="Moves" value={brief.metrics.momentum_events} />
            </div>
          </div>

          {brief.executive_narrative?.length > 0 && (
            <div className="bg-white border border-brand p-4 mb-4" data-testid="brief-exec-narrative">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-brand" />
                <h3 className="font-heading text-sm font-semibold uppercase tracking-tight">Chief-of-Staff Read</h3>
                <span className="text-[10px] font-semibold uppercase tracking-wider border border-brand text-brand px-1.5 py-0.5">Claude Sonnet 4.6</span>
              </div>
              <ul className="space-y-1.5">
                {brief.executive_narrative.map((n, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-ink-muted"><span className="text-brand">—</span> {n}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          <div className="bg-brand/5 border border-brand p-4 mb-4" data-testid="brief-recommendations">
            <div className="flex items-center gap-2 mb-2"><Lightbulb className="w-4 h-4 text-brand" /><h3 className="font-heading text-sm font-semibold uppercase tracking-tight">Do This First</h3></div>
            <ul className="space-y-1.5">
              {brief.sections.recommendations.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-ink"><ArrowRight className="w-3.5 h-3.5 mt-1 text-brand shrink-0" /> {r}</li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Decisions needed */}
            <Card testId="brief-decisions">
              <CardHeader title="Decisions Need You" icon={ShieldCheck} action={<span className="text-[11px] font-mono text-ink-faint">{brief.sections.decisions_needed.length}</span>} />
              <div className="divide-y divide-border">
                {brief.sections.decisions_needed.map((d) => (
                  <button key={d.approval_id} onClick={() => navigate(`/approvals/${d.approval_id}`)} data-testid={`brief-decision-${d.approval_id}`}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-muted transition-colors">
                    <PriorityBadge value={d.priority} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-ink truncate">{d.title}</div>
                      <div className="text-xs text-ink-muted">{d.client} · waiting {Math.round(d.age_hours)}h</div>
                    </div>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider border px-1.5 py-0.5 ${PRED_STYLE[d.prediction.action]}`} title={d.prediction.rationale}>
                      {d.prediction.label} {d.prediction.confidence}%
                    </span>
                  </button>
                ))}
                {brief.sections.decisions_needed.length === 0 && <div className="px-4 py-6 text-sm text-ink-muted">No decisions waiting. </div>}
              </div>
            </Card>

            {/* Deadline risk */}
            <Card testId="brief-deadline-risk">
              <CardHeader title="Deadline Risk" icon={CalendarClock} />
              <div className="divide-y divide-border">
                {brief.sections.deadline_risk.map((s) => (
                  <button key={s.entity_id} onClick={() => navigate(`/jobs/${s.entity_id}`)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-muted">
                    <span className={`w-1.5 h-8 ${s.days_left < 0 ? "bg-st-critical" : "bg-st-warning"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-ink truncate">{s.name}</div>
                      <div className="text-xs text-ink-muted">{s.reasons.join(" · ")}</div>
                    </div>
                    <span className={`text-xs font-mono font-semibold ${s.days_left < 0 ? "text-st-critical" : "text-st-warning"}`}>{s.days_left < 0 ? `${Math.abs(s.days_left)}d late` : `${s.days_left}d`}</span>
                  </button>
                ))}
                {brief.sections.deadline_risk.length === 0 && <div className="px-4 py-6 text-sm text-ink-muted">Nothing at deadline risk.</div>}
              </div>
            </Card>

            {/* Blocked / stale */}
            <Card testId="brief-blocked">
              <CardHeader title="Blocked / Stale" icon={Ban} />
              <div className="divide-y divide-border">
                {brief.sections.blocked_or_stale.map((s) => (
                  <button key={s.entity_id} onClick={() => navigate(s.entity_type === "approval" ? `/approvals/${s.entity_id}` : `/jobs/${s.entity_id}`)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-muted">
                    <AlertTriangle className={`w-4 h-4 shrink-0 ${s.severity === "critical" ? "text-st-critical" : "text-st-warning"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-ink truncate">{s.name}</div>
                      <div className="text-xs text-ink-muted">{s.reasons.join(" · ")}</div>
                    </div>
                  </button>
                ))}
                {brief.sections.blocked_or_stale.length === 0 && <div className="px-4 py-6 text-sm text-ink-muted">Nothing stale — momentum is healthy.</div>}
              </div>
            </Card>

            {/* Momentum */}
            <Card testId="brief-momentum">
              <CardHeader title="What Moved" icon={TrendingUp} />
              <div className="divide-y divide-border max-h-80 overflow-y-auto">
                {brief.sections.momentum.map((m, i) => (
                  <div key={i} className="flex items-start gap-3 px-4 py-2.5">
                    <CheckCircle2 className="w-3.5 h-3.5 text-st-success mt-0.5 shrink-0" />
                    <div className="flex-1"><div className="text-sm text-ink">{m.summary}</div><div className="text-[11px] text-ink-faint mt-0.5">{m.actor} · {relativeTime(m.at)}</div></div>
                  </div>
                ))}
                {brief.sections.momentum.length === 0 && <div className="px-4 py-6 text-sm text-ink-muted">No activity in this window.</div>}
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function Chip({ label, value, warn }) {
  return (
    <div className={`px-3 py-2 ${warn ? "bg-st-warning/20" : "bg-white/10"}`}>
      <div className="font-heading text-xl font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-white/60">{label}</div>
    </div>
  );
}
